/**
 * Putting a client question in front of its client from the Inquiry Queue
 * (#4211 the PDF, #17 the email): the wire shapes of `client-inquiry-pdfs`
 * (packages/api/src/services/organization/client-inquiries/client-inquiry-pdfs.ts)
 * and the sentences the panel says about them. Pure, so every word is pinned by
 * inquiryDelivery.types.test.ts.
 */
import { sendOutcomeWording, type Severity } from "../../meetingIntelligence/MeetingDetail/meetingRecap.types";
import { missingForRelease, type InquiryRecord } from "./inquiryQueue.types";

export { sendStatusLabel as emailStatusLabel } from "../../meetingIntelligence/MeetingDetail/meetingRecap.types";

export interface InquiryPdf {
  inquiryId: number;
  documentId: string;
  fileName: string;
  bytes: number;
  generatedAt: string | null;
  generatedBy: { userId: number | null; name: string | null };
}

export interface InquiryPdfResult extends InquiryPdf {
  /** The app link the PDF tells the client to answer in, or null when it says to reply. */
  appLink: string | null;
}

/**
 * Why this question cannot go to its client as a PDF or an email yet, in one
 * sentence; null when it can. Mirrors the API's `clientSendBlocker`, which
 * decides — this only says why a button is greyed out before anyone presses it.
 */
export const sendBlocker = (r: InquiryRecord): string | null => {
  if (r.client.id === null) return "This question has no client, so there is nobody to send it to.";
  if (r.stage !== "internal" && r.stage !== "released") return `This question is ${r.stage}, so it is no longer sent to the client.`;
  if (r.multi) return "This question allows more than one option, so it cannot be sent to the client as it is.";
  if (!r.clientText) return "Write the client's text first (Write the client's text).";
  const missing = missingForRelease(r.clientText, r.options);
  if (missing.length) return `The client's text is not finished: ${missing.join(", ")} still to write.`;
  return null;
};

/** One PDF in the list: who made it and when. */
export const pdfLine = (p: InquiryPdf): string => {
  const when = p.generatedAt ? new Date(p.generatedAt) : null;
  const at = when && Number.isFinite(when.getTime()) ? when.toLocaleString() : null;
  return [p.fileName, p.generatedBy.name ? `made by ${p.generatedBy.name}` : null, at].filter(Boolean).join(" · ");
};

// ---- the email (#17) ---------------------------------------------------------
// The wire shapes of `client-inquiry-emails`
// (packages/api/src/services/organization/client-inquiries/client-inquiry-emails.ts).

export interface InquiryEmailRecipient {
  email: string;
  name: string;
  title: string | null;
  isPrimary: boolean;
}

export interface InquiryEmailSend {
  id: number;
  to: string[];
  subject: string;
  /** accepted | refused | error — SendGrid's answer, never "delivered". */
  providerStatus: string;
  sandbox: boolean;
  problem: string | null;
  sentBy: string | null;
  createdAt: string | null;
}

export interface InquiryEmailDraft {
  /** The question's id: one cache entry per question. */
  id: number;
  inquiryId: number;
  clientId: string | null;
  clientName: string | null;
  stage: string;
  optionCount: number;
  blocked: string | null;
  /** The link into the client's app the draft carries, or why there is none. */
  link: { url: string | null; problem: string | null };
  subject: string;
  text: string;
  /** The client's own contacts; nobody is ticked for the sender. */
  recipients: InquiryEmailRecipient[];
  otherClients: string[];
  email: { configured: boolean; sandbox: boolean };
  sends: InquiryEmailSend[];
}

export interface InquiryEmailResult {
  inquiryId: number;
  documentId: string;
  pdfDocumentId: string | null;
  emailSendId: number | null;
  providerStatus: "accepted" | "refused" | "error";
  accepted: boolean;
  sandbox: boolean;
  problem: string | null;
  addressed: number;
  acceptedFor: number;
}

/**
 * What one send's answer means — the recap's words for "the email", never
 * "delivered" — and, for a question not yet released, what to do next: HQ does
 * not release it by itself, and a reply can only be recorded once it is.
 */
export function emailOutcomeWording(r: InquiryEmailResult, stage: string): { severity: Severity; text: string } {
  const said = sendOutcomeWording(r, "the email");
  if (!r.accepted || r.sandbox || stage !== "internal") return said;
  return {
    ...said,
    text: `${said.text} The question is still Internal: release it ("As the PDF") once it has gone, so their reply can be recorded.`,
  };
}
